import { useMemo, useState } from 'react';
import { Header, Message } from 'semantic-ui-react';
import S from '../core.js';
import { managerOf } from '../constants.js';
import { statusOf } from '../approvals.js';
import { hasFigures, periodOf, viewLayout } from '../periods.js';
import ApprovalPanel from './ApprovalPanel.jsx';
import ApprovalHistory from './ApprovalHistory.jsx';
import Preview from './Preview.jsx';
import Tabs from './Tabs.jsx';

/* One report for one period: the title, the Preview / Audit tabs, the two approval steps (with the downloads once both are done)
 * and the preview itself. Used by the Reports screen (the period due, by the report's frequency) and by the Historic screen (a whole year).
 * `picker` is whatever period selectors the caller wants above the approval steps. */
export default function ReportBody({ model, sel, approvals, user, busy, storage, picker, onApprove, onDownload }) {
  const [tab, setTab] = useState('preview');
  const { report } = model;
  const base = useMemo(() => S.buildLayout(model.report, model.figures), [model]);
  const period = useMemo(() => periodOf(sel), [sel]);
  const status = useMemo(() => statusOf(approvals, report, period), [approvals, report, period]);
  const layout = useMemo(() => viewLayout(base, { ...period, cols: Array.from({ length: base.heads.length }, (_, i) => i) }, status), [base, period, status]);   // the preview always has every month, the quarter totals and the year
  const figuresHere = useMemo(() => hasFigures(base, period), [base, period]);
  const history = useMemo(() => approvals.filter((a) => a.settlement_report_id === report.settlement_report_id && a.report_year === report.report_year && a.as_at_date === report.as_at_date), [approvals, report]);
  return (
    <>
      <Header as="h2" className="titlebar">
        {report.settlement_report_id}
        <Header.Subheader>
          {report.client_name} · {report.report_year} · as at <time dateTime={report.as_at_date}>{S.dmy(report.as_at_date)}</time> · {managerOf(report)}
        </Header.Subheader>
      </Header>
      <Tabs idPrefix="report" label="Report sections" value={tab} onChange={setTab} tabs={[['preview', 'Preview'], ['audit', 'Audit']]}>
        {tab === 'audit' ? (
          <ApprovalHistory rows={history} />
        ) : (
          <>
            {picker}
            <ApprovalPanel period={period} report={report} status={status} user={user} busy={busy} hasFigures={figuresHere}
              onApprove={(level) => onApprove(level, report, period)} onDownload={(kind) => onDownload(kind, { report, period, layout, status })} storage={storage} />
            {!figuresHere && <Message info role="status">This report has no figures for {period.title} (it runs to {S.dmy(report.as_at_date)}).</Message>}
            <Preview layout={layout} />
          </>
        )}
      </Tabs>
    </>
  );
}
